import * as THREE from "three";
import { StickFigure } from "./StickFigure";
import { WindowCycle } from "./WindowCycle";

/** Builds the cyberpunk-minimalist room scene and returns handles to interact with it. */
export function buildRoom({ videoEl } = {}) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf5f5f2);
  scene.fog = new THREE.Fog(0xf5f5f2, 12, 24);

  // Colors
  const PAPER = 0xf5f5f2;
  const INK = 0x0a0a0f;
  const CYAN = 0x00d3d9;
  const MAGENTA = 0xff2e88;
  const LIME = 0xc8ff2e;

  // Dimensions
  const W = 8, H = 4.2, D = 8;
  const HALF_W = W / 2, HALF_D = D / 2;

  // ---------- LIGHTING ----------
  scene.add(new THREE.AmbientLight(0xffffff, 0.55));
  const key = new THREE.DirectionalLight(0xffffff, 0.6);
  key.position.set(2, 4, 3);
  scene.add(key);

  // Neon ceiling strip (long thin emissive box)
  const stripMat = new THREE.MeshBasicMaterial({ color: CYAN });
  const strip = new THREE.Mesh(new THREE.BoxGeometry(W * 0.6, 0.06, 0.14), stripMat);
  strip.position.set(0, H - 0.08, 0);
  scene.add(strip);

  const stripLight1 = new THREE.PointLight(CYAN, 1.2, 12, 2);
  stripLight1.position.set(-1.5, H - 0.4, 0);
  scene.add(stripLight1);
  const stripLight2 = new THREE.PointLight(MAGENTA, 0.9, 10, 2);
  stripLight2.position.set(2, H - 0.4, 1.5);
  scene.add(stripLight2);

  // ---------- MATERIALS ----------
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xefefe8, roughness: 0.88, metalness: 0.02 });
  const floorMat = new THREE.MeshStandardMaterial({ color: 0xe6e6df, roughness: 0.7, metalness: 0.05 });
  const ceilMat = new THREE.MeshStandardMaterial({ color: 0xf5f5f2, roughness: 0.95, metalness: 0 });

  // ---------- SHELL ----------
  // Floor
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(W, D), floorMat);
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  // Ceiling
  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(W, D), ceilMat);
  ceil.rotation.x = Math.PI / 2;
  ceil.position.y = H;
  scene.add(ceil);

  // Back wall (behind user, +Z)
  const backWall = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat);
  backWall.position.set(0, H / 2, HALF_D);
  backWall.rotation.y = Math.PI;
  scene.add(backWall);

  // Front wall (-Z) — hologram screen wall
  const frontWall = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat);
  frontWall.position.set(0, H / 2, -HALF_D);
  scene.add(frontWall);

  // Left wall (-X) — window
  const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(D, H), wallMat);
  leftWall.position.set(-HALF_W, H / 2, 0);
  leftWall.rotation.y = Math.PI / 2;
  scene.add(leftWall);

  // Right wall (+X) — record player
  const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(D, H), wallMat);
  rightWall.position.set(HALF_W, H / 2, 0);
  rightWall.rotation.y = -Math.PI / 2;
  scene.add(rightWall);

  // ---------- FLOOR GRID (subtle) ----------
  const grid = new THREE.GridHelper(W, 12, 0xbfbfb8, 0xd6d6cf);
  grid.position.y = 0.001;
  scene.add(grid);

  // ---------- FRONT WALL: stick-figure dance screen ----------
  const stickFigure = new StickFigure({ width: 1152, height: 512 });
  const holo = new THREE.Mesh(
    new THREE.PlaneGeometry(5.8, 2.58),
    new THREE.MeshBasicMaterial({ map: stickFigure.texture })
  );
  holo.position.set(0, H / 2 + 0.1, -HALF_D + 0.02);
  scene.add(holo);

  // Frame around the dance screen
  const holoFrame = new THREE.Mesh(
    new THREE.PlaneGeometry(6.0, 2.78),
    new THREE.MeshBasicMaterial({ color: INK })
  );
  holoFrame.position.set(0, H / 2 + 0.1, -HALF_D + 0.015);
  scene.add(holoFrame);

  // Neon underline
  const holoLine = new THREE.Mesh(
    new THREE.PlaneGeometry(5.8, 0.04),
    new THREE.MeshBasicMaterial({ color: MAGENTA })
  );
  holoLine.position.set(0, H / 2 - 1.22, -HALF_D + 0.03);
  scene.add(holoLine);

  const setPoseLandmarks = (lm) => stickFigure.setLandmarks(lm);

  // ---------- LEFT WALL: window with cityscape parallax ----------
  const windowCycle = new WindowCycle();
  const winMat = new THREE.MeshBasicMaterial({ map: windowCycle.texture });
  const windowMesh = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 2.2), winMat);
  windowMesh.position.set(-HALF_W + 0.02, H / 2 + 0.05, 0);
  windowMesh.rotation.y = Math.PI / 2;
  scene.add(windowMesh);

  // Window frame (dark)
  const winFrame = new THREE.Mesh(
    new THREE.PlaneGeometry(3.5, 2.5),
    new THREE.MeshBasicMaterial({ color: INK })
  );
  winFrame.position.set(-HALF_W + 0.012, H / 2 + 0.05, 0);
  winFrame.rotation.y = Math.PI / 2;
  scene.add(winFrame);

  // Window mullions (cross)
  const mullMat = new THREE.MeshBasicMaterial({ color: INK });
  const mullV = new THREE.Mesh(new THREE.PlaneGeometry(0.05, 2.2), mullMat);
  mullV.position.set(-HALF_W + 0.03, H / 2 + 0.05, 0);
  mullV.rotation.y = Math.PI / 2;
  scene.add(mullV);
  const mullH = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 0.05), mullMat);
  mullH.position.set(-HALF_W + 0.03, H / 2 + 0.05, 0);
  mullH.rotation.y = Math.PI / 2;
  scene.add(mullH);

  // Window sill (small shelf below)
  const sill = new THREE.Mesh(
    new THREE.BoxGeometry(0.15, 0.08, 3.6),
    new THREE.MeshStandardMaterial({ color: INK, roughness: 0.6 })
  );
  sill.position.set(-HALF_W + 0.08, H / 2 - 1.15, 0);
  scene.add(sill);

  // Small potted plant on sill (deco)
  const pot = new THREE.Mesh(
    new THREE.CylinderGeometry(0.14, 0.1, 0.22, 12),
    new THREE.MeshStandardMaterial({ color: LIME, roughness: 0.6 })
  );
  pot.position.set(-HALF_W + 0.14, H / 2 - 0.98, 1.1);
  scene.add(pot);
  const leaves = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.24, 0),
    new THREE.MeshStandardMaterial({ color: 0x2e6a3a, roughness: 0.9, flatShading: true })
  );
  leaves.position.set(-HALF_W + 0.14, H / 2 - 0.7, 1.1);
  scene.add(leaves);

  // ---------- RIGHT WALL: record player + shelf ----------
  const shelfMat = new THREE.MeshStandardMaterial({ color: INK, roughness: 0.5 });
  const shelf = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, 2.2), shelfMat);
  shelf.position.set(HALF_W - 0.28, H / 2 - 0.2, 0);
  scene.add(shelf);

  // Record player base
  const rpBase = new THREE.Mesh(
    new THREE.BoxGeometry(0.45, 0.09, 1.4),
    new THREE.MeshStandardMaterial({ color: 0xefefe8, roughness: 0.6, metalness: 0.1 })
  );
  rpBase.position.set(HALF_W - 0.3, H / 2 - 0.11, 0);
  scene.add(rpBase);

  // Turntable (rotating disc)
  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.55, 0.02, 48),
    new THREE.MeshStandardMaterial({ color: 0x0a0a0f, roughness: 0.35, metalness: 0.6 })
  );
  disc.position.set(HALF_W - 0.36, H / 2 - 0.05, 0);
  disc.rotation.z = 0; // vertical axis (Y) is default cylinder axis
  scene.add(disc);

  // Label (magenta center)
  const label = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.18, 0.022, 32),
    new THREE.MeshStandardMaterial({ color: MAGENTA, roughness: 0.4 })
  );
  label.position.set(HALF_W - 0.36, H / 2 - 0.038, 0);
  scene.add(label);

  // Spindle
  const spindle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 0.08, 12),
    new THREE.MeshStandardMaterial({ color: 0x555, roughness: 0.3, metalness: 0.9 })
  );
  spindle.position.set(HALF_W - 0.36, H / 2 - 0.01, 0);
  scene.add(spindle);

  // Tonearm (thin box at an angle)
  const arm = new THREE.Mesh(
    new THREE.BoxGeometry(0.62, 0.025, 0.025),
    new THREE.MeshStandardMaterial({ color: 0x2a2a35, roughness: 0.3, metalness: 0.8 })
  );
  arm.position.set(HALF_W - 0.36, H / 2 - 0.02, -0.4);
  arm.rotation.y = -0.6;
  scene.add(arm);

  // Small poster above the record player
  const posterCanvas = document.createElement("canvas");
  posterCanvas.width = 512;
  posterCanvas.height = 720;
  const posterCtx = posterCanvas.getContext("2d");
  drawPoster(posterCtx, posterCanvas.width, posterCanvas.height);
  const posterTex = new THREE.CanvasTexture(posterCanvas);
  const poster = new THREE.Mesh(
    new THREE.PlaneGeometry(0.9, 1.25),
    new THREE.MeshBasicMaterial({ map: posterTex })
  );
  poster.position.set(HALF_W - 0.03, H / 2 + 0.9, 0);
  poster.rotation.y = -Math.PI / 2;
  scene.add(poster);

  // ---------- BACK WALL (behind camera): DOOR + serif sign ----------
  // Door recessed into wall
  const doorW = 1.6, doorH = 2.6;
  const doorFrame = new THREE.Mesh(
    new THREE.PlaneGeometry(doorW + 0.16, doorH + 0.16),
    new THREE.MeshBasicMaterial({ color: INK })
  );
  doorFrame.position.set(0, doorH / 2 + 0.02, HALF_D - 0.02);
  doorFrame.rotation.y = Math.PI;
  scene.add(doorFrame);

  const doorPanel = new THREE.Mesh(
    new THREE.PlaneGeometry(doorW, doorH),
    new THREE.MeshStandardMaterial({ color: 0x151520, roughness: 0.35, metalness: 0.4 })
  );
  doorPanel.position.set(0, doorH / 2 + 0.02, HALF_D - 0.03);
  doorPanel.rotation.y = Math.PI;
  scene.add(doorPanel);

  // Vertical seam
  const doorSeam = new THREE.Mesh(
    new THREE.PlaneGeometry(0.02, doorH * 0.85),
    new THREE.MeshBasicMaterial({ color: 0x2a2a35 })
  );
  doorSeam.position.set(0.35, doorH / 2 - 0.15 + 0.02, HALF_D - 0.04);
  doorSeam.rotation.y = Math.PI;
  scene.add(doorSeam);

  // Neon strip above door
  const doorStrip = new THREE.Mesh(
    new THREE.PlaneGeometry(doorW * 0.9, 0.035),
    new THREE.MeshBasicMaterial({ color: MAGENTA })
  );
  doorStrip.position.set(0, doorH + 0.12, HALF_D - 0.04);
  doorStrip.rotation.y = Math.PI;
  scene.add(doorStrip);

  // Door handle
  const doorHandle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04, 0.04, 0.14, 12),
    new THREE.MeshStandardMaterial({ color: CYAN, roughness: 0.2, metalness: 0.9 })
  );
  doorHandle.position.set(-0.55, doorH / 2 + 0.02, HALF_D - 0.05);
  doorHandle.rotation.z = Math.PI / 2;
  scene.add(doorHandle);

  // Small keypad panel next to door
  const keypad = new THREE.Mesh(
    new THREE.PlaneGeometry(0.22, 0.34),
    new THREE.MeshBasicMaterial({ color: 0x0a0a0f })
  );
  keypad.position.set(-1.05, doorH / 2 + 0.02, HALF_D - 0.03);
  keypad.rotation.y = Math.PI;
  scene.add(keypad);
  const keypadLight = new THREE.Mesh(
    new THREE.PlaneGeometry(0.14, 0.03),
    new THREE.MeshBasicMaterial({ color: LIME })
  );
  keypadLight.position.set(-1.05, doorH / 2 + 0.12, HALF_D - 0.04);
  keypadLight.rotation.y = Math.PI;
  scene.add(keypadLight);

  // Serif sign (kept, moved up)
  const signCanvas = document.createElement("canvas");
  signCanvas.width = 1024;
  signCanvas.height = 256;
  const signCtx = signCanvas.getContext("2d");
  drawBackSign(signCtx, signCanvas.width, signCanvas.height);
  const signTex = new THREE.CanvasTexture(signCanvas);
  const sign = new THREE.Mesh(
    new THREE.PlaneGeometry(4.2, 1.05),
    new THREE.MeshBasicMaterial({ map: signTex, transparent: true })
  );
  sign.position.set(0, H - 0.45, HALF_D - 0.02);
  sign.rotation.y = Math.PI;
  scene.add(sign);

  // ---------- ANIMATED UPDATE FN ----------
  const state = { t: 0 };

  function drawPoster(c, w, h) {
    c.fillStyle = "#0a0a0f";
    c.fillRect(0, 0, w, h);
    c.fillStyle = "#ff2e88";
    c.fillRect(0, 0, w, 12);
    c.fillStyle = "#c8ff2e";
    c.fillRect(0, h - 12, w, 12);

    c.fillStyle = "#f5f5f2";
    c.font = "italic 88px 'Instrument Serif', serif";
    c.fillText("side b.", 40, 200);

    c.fillStyle = "#00d3d9";
    c.font = "700 34px 'JetBrains Mono', monospace";
    c.fillText("NIGHT LOOP //", 40, 280);

    // circular record
    c.strokeStyle = "#f5f5f2";
    c.lineWidth = 3;
    c.beginPath();
    c.arc(w / 2, h / 2 + 120, 130, 0, Math.PI * 2);
    c.stroke();
    for (let r = 40; r < 130; r += 10) {
      c.strokeStyle = "rgba(245,245,242,0.4)";
      c.beginPath();
      c.arc(w / 2, h / 2 + 120, r, 0, Math.PI * 2);
      c.stroke();
    }
    c.fillStyle = "#ff2e88";
    c.beginPath();
    c.arc(w / 2, h / 2 + 120, 34, 0, Math.PI * 2);
    c.fill();
  }

  function drawBackSign(c, w, h) {
    c.clearRect(0, 0, w, h);
    c.fillStyle = "#0a0a0f";
    c.font = "italic 96px 'Instrument Serif', serif";
    c.fillText("look around, quietly.", 40, 130);
    c.fillStyle = "#ff2e88";
    c.font = "700 32px 'JetBrains Mono', monospace";
    c.fillText("// DARD-E-DISCO.01", 40, 200);
  }

  function update(dt) {
    state.t += dt;
    // spin the record
    disc.rotation.y += dt * 3.0;
    label.rotation.y = disc.rotation.y;

    // tonearm subtle wobble
    arm.rotation.y = -0.6 + Math.sin(state.t * 0.6) * 0.02;

    // stick figure dance screen
    stickFigure.update(state.t);

    // window canvas
    windowCycle.update(state.t);

    // pulse neon strip color slightly
    const pulse = 0.5 + 0.5 * Math.sin(state.t * 1.6);
    stripLight1.intensity = 0.9 + pulse * 0.4;
  }

  // World positions of sound sources
  const soundSources = {
    window: new THREE.Vector3(-HALF_W, H / 2, 0),
    recordPlayer: new THREE.Vector3(HALF_W, H / 2 - 0.05, 0),
  };

  return { scene, update, soundSources, setPoseLandmarks };
}
